import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, FloppyDisk, WarningCircle, Scales, LockKey, FileText } from "@phosphor-icons/react";

const TABS = ["impressum", "datenschutz", "agb"];

export default function AdminLegal() {
    const { t, user } = useApp();
    const [tab, setTab] = useState("impressum");
    const [docs, setDocs] = useState({});
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState(false);
    const [access, setAccess] = useState(null); // { is_admin, email }

    useEffect(() => {
        (async () => {
            try {
                const [r, docsR] = await Promise.all([
                    api.get("/legal/_/is-admin"),
                    api.get("/legal"),
                ]);
                setAccess(r.data);
                setDocs(docsR.data);
                const d = {};
                Object.keys(docsR.data).forEach((k) => { d[k] = docsR.data[k].content; });
                setDrafts(d);
            } catch (e) {
                setAccess({ is_admin: false });
            }
        })();
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.put(`/legal/${tab}`, { content: drafts[tab] });
            setDocs((d) => ({ ...d, [tab]: r.data }));
            toast.success(t("admin.saved"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Fehler");
        } finally { setSaving(false); }
    };

    if (access === null) {
        return <AppShell><div className="p-10 text-shield-muted">{t("common.loading")}</div></AppShell>;
    }

    if (!access.is_admin) {
        return (
            <AppShell>
                <div className="mx-auto max-w-lg px-6 py-16 text-center">
                    <div className="card-soft p-10">
                        <WarningCircle size={40} weight="duotone" className="mx-auto text-shield-error" aria-hidden="true" />
                        <h1 className="mt-4 font-heading font-bold text-shield-ink text-2xl">{t("admin.noAccess")}</h1>
                        <p className="mt-2 text-sm text-shield-muted">{user?.email}</p>
                    </div>
                </div>
            </AppShell>
        );
    }

    const tabIcons = { impressum: Scales, datenschutz: LockKey, agb: FileText };

    return (
        <AppShell>
            <div className="mx-auto max-w-5xl px-6 lg:px-10 py-10 lg:py-14">
                <div className="flex items-center gap-3">
                    <ShieldCheck size={28} weight="duotone" className="text-shield-forest" aria-hidden="true" />
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">Master-Zugang</div>
                        <h1 className="mt-1 font-heading font-bold text-shield-ink text-2xl sm:text-3xl" data-testid="admin-title">
                            {t("admin.title")}
                        </h1>
                    </div>
                </div>
                <p className="mt-2 text-shield-muted">{t("admin.sub")}</p>

                <div className="mt-8 flex gap-2 flex-wrap">
                    {TABS.map((k) => {
                        const Icon = tabIcons[k];
                        const active = tab === k;
                        return (
                            <button
                                key={k}
                                onClick={() => setTab(k)}
                                className={`inline-flex items-center gap-2 h-11 px-5 pill font-semibold transition-colors ${active ? "bg-shield-forest text-white" : "bg-white border border-shield-line text-shield-ink hover:bg-shield-sage"}`}
                                data-testid={`admin-tab-${k}`}
                            >
                                <Icon size={16} weight="bold" aria-hidden="true" />
                                {t(`legal.tabs.${k}`)}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-6 card-soft p-6 lg:p-8">
                    <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-shield-muted mb-2">
                        Markdown-Editor · {t(`legal.tabs.${tab}`)}
                    </label>
                    <textarea
                        value={drafts[tab] ?? ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [tab]: e.target.value }))}
                        className="w-full min-h-[500px] rounded-xl border-2 border-shield-line p-4 font-mono text-sm bg-shield-sage/20 focus:border-shield-forest focus:outline-none focus:ring-4 focus:ring-shield-forest/20"
                        data-testid={`admin-textarea-${tab}`}
                    />
                    <div className="mt-4 flex items-center justify-between">
                        <div className="text-xs text-shield-muted">
                            {docs?.[tab]?.updated_at && (
                                <>Zuletzt: {new Date(docs[tab].updated_at).toLocaleString()} · von {docs[tab].updated_by || "—"}</>
                            )}
                        </div>
                        <button
                            onClick={save}
                            disabled={saving}
                            className="inline-flex items-center gap-2 h-12 px-6 pill bg-shield-terracotta text-white font-semibold hover:bg-shield-terracottaDeep transition-colors disabled:opacity-70"
                            data-testid={`admin-save-${tab}`}
                        >
                            <FloppyDisk size={16} weight="bold" aria-hidden="true" />
                            {saving ? t("common.loading") : t("admin.save")}
                        </button>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
